import { useCallback, useEffect, useRef, useState } from "react";
import useUIBp from "../../Hooks/useUIBoilerplate";
import { UIactions } from "../../store/slices/UI";
import { workerActions } from "../../store/slices/workers";
import notEmptyPassword from "../../utility/validations/notEmptyPassword";
import notEmptyUser from "../../utility/validations/notEmptyUser";
import LegacyBtn from "./Components/Buttons/LegacyBtn";
import Locale from "./Components/Buttons/Locale";
import LoginBtn from "./Components/Buttons/LoginBtn";
import ResetBtn from "./Components/Buttons/ResetBtn";
import Header from "./Components/Header";
import LegacyDialog from "./Components/Legacy/LegacyDialog";
import Logo from "./Components/Logo";
import Password, { loginPasswordID } from "./Components/Password";
import TFAView from "./Components/TFAView/TFAView";
import User, { loginUserID } from "./Components/User/User";

import { Grid2 } from "@mui/material";
import loginToCloner, { loginWrkID, TFA_ID } from "./Actions/loginToCloner";
import { gauthWrkID } from "./Actions/loginWithGAuth";

const LOGIN_COOLDOWN_MS = 2000;

// Defining the Login component
const Login = () => {
  // Importing necessary dependencies
  const { auth, dispatch, navigate, UI, settings } = useUIBp();
  const { admin, group } = auth;
  const locale = settings.locale ?? "es-CL";
  const { token } = auth; // This will allow us to redirect to the dashboard if the token exists
  const userField = UI[loginUserID] ?? {};
  const passwordField = UI[loginPasswordID] ?? {};
  const twofactor = UI[TFA_ID] ?? false;
  const disabled = twofactor;
  const height = window.innerHeight;

  // Login cooldown state to prevent brute force
  const [loginCooldown, setLoginCooldown] = useState(false);
  const cooldownTimer = useRef(null);

  // Clearing workers and UI state on unmount
  useEffect(() => {
    return () => {
      dispatch(workerActions.clear(loginWrkID));
      dispatch(workerActions.clear(gauthWrkID));
      clearTimeout(cooldownTimer.current);
    };
  }, [dispatch]);

  // Redirecting to dashboard if token exists
  useEffect(() => {
    if (token) {
      if (!!admin) {
        navigate(`/dashboard/reports?group=${group}`, { replace: true });
      } else {
        navigate(`/dashboard/files`, { replace: true });
      }
      dispatch(UIactions.clear(loginUserID));
      dispatch(UIactions.clear(loginPasswordID));
      dispatch(UIactions.clear(TFA_ID));
    }
  }, [dispatch, navigate, token, group, admin]);

  // Hard validation function
  const hardValidation = () => {
    const value = { id: loginUserID, fn: notEmptyUser, force: true, locale };
    dispatch(UIactions.validateField(value));
    const value2 = {
      id: loginPasswordID,
      fn: notEmptyPassword,
      force: true,
      locale,
    };
    dispatch(UIactions.validateField(value2));
  };

  // Login function
  const login = useCallback(() => {
    if (loginCooldown) return;
    if (!userField.error && !passwordField.error && userField.value && passwordField.value) {
      setLoginCooldown(true);
      cooldownTimer.current = setTimeout(() => {
        setLoginCooldown(false);
      }, LOGIN_COOLDOWN_MS);

      const payload = {
        user: userField.value,
        pass: passwordField.value,
        locale,
      };
      dispatch(loginToCloner(payload));
    }
  }, [loginCooldown, userField, passwordField, locale, dispatch]);

  // Rendering the Login component
  return (
    <Grid2 container id="login-container" sx={{ width: "100vw", height }} alignContent="space-between">
      <Locale />
      <Grid2 container size={12} rowGap={3}>
        <Logo />
        <Header {...{ locale }} />
        <User {...{ disabled, hardValidation, login }} />
        <Password {...{ disabled, hardValidation, login }} />
        {!twofactor ? (
          <Grid2 container {...{ size: 12, justifyContent: "center", rowGap: 2 }}>
            <LoginBtn {...{ hardValidation, login, loginCooldown }} />
            <ResetBtn />
          </Grid2>
        ) : (
          <TFAView />
        )}
      </Grid2>
      <Grid2
        container
        {...{
          size: 12,
          sx: { padding: 1 },
          justifyContent: "end",
        }}
      >
        <LegacyBtn />
      </Grid2>
      <LegacyDialog />
    </Grid2>
  );
};

export default Login;
